/* eslint-disable react-refresh/only-export-components */
import {
  DimeAccordion,
  DimeAccordionContent,
  DimeAccordionItem,
  DimeAccordionTrigger,
  DimeButton,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { CardDocsPreview } from './-CardDocsPreview.js';
import { CardDocsSection } from './-CardDocsSection.js';

export const Route = createFileRoute('/design-system/accordion')({
  component: AccordionDocs,
});

function SingleExample() {
  return (
    <DimeAccordion type="single" collapsible className="max-w-md">
      <DimeAccordionItem value="item-1">
        <DimeAccordionTrigger>What is an accordion?</DimeAccordionTrigger>
        <DimeAccordionContent>
          A list of headings that each reveal a panel. Only one panel is open at a time, and{' '}
          <code className="font-mono">collapsible</code> lets you close the open one.
        </DimeAccordionContent>
      </DimeAccordionItem>
      <DimeAccordionItem value="item-2">
        <DimeAccordionTrigger>When should I use it?</DimeAccordionTrigger>
        <DimeAccordionContent>
          FAQs, settings groups, or any list where opening one item should close the others.
        </DimeAccordionContent>
      </DimeAccordionItem>
    </DimeAccordion>
  );
}

function MultipleExample() {
  return (
    <DimeAccordion type="multiple" className="max-w-md">
      <DimeAccordionItem value="item-1">
        <DimeAccordionTrigger>Billing</DimeAccordionTrigger>
        <DimeAccordionContent>Invoice address, payment terms, and tax IDs.</DimeAccordionContent>
      </DimeAccordionItem>
      <DimeAccordionItem value="item-2">
        <DimeAccordionTrigger>Shipping</DimeAccordionTrigger>
        <DimeAccordionContent>Default ship-to, carrier, and delivery notes.</DimeAccordionContent>
      </DimeAccordionItem>
      <DimeAccordionItem value="item-3">
        <DimeAccordionTrigger>Contacts</DimeAccordionTrigger>
        <DimeAccordionContent>Primary and additional people on the account.</DimeAccordionContent>
      </DimeAccordionItem>
    </DimeAccordion>
  );
}

function CustomTriggerExample() {
  return (
    <DimeAccordion type="multiple" className="max-w-md">
      <DimeAccordionItem value="jane" className="border-0">
        <div className="flex items-center gap-2 py-2">
          <p className="min-w-0 flex-1 truncate font-medium">Jane Doe</p>
          <DimeAccordionTrigger asChild>
            <DimeButton
              type="button"
              variant="outline"
              size="icon-sm"
              aria-label="Details for Jane Doe"
            >
              <ChevronDown />
            </DimeButton>
          </DimeAccordionTrigger>
        </div>
        <DimeAccordionContent>jane@example.com</DimeAccordionContent>
      </DimeAccordionItem>
    </DimeAccordion>
  );
}

function AccordionDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Accordion</h1>
      <p className="mb-8 text-muted-foreground">
        A list of expandable items. Use <code className="font-mono">type=&quot;single&quot;</code>{' '}
        so only one panel is open, or <code className="font-mono">type=&quot;multiple&quot;</code>{' '}
        so several can stay open together.
      </p>

      <CardDocsSection title="Single">
        <CardDocsPreview>
          <SingleExample />
        </CardDocsPreview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">type=&quot;single&quot;</code> plus{' '}
          <code className="font-mono">collapsible</code> — opening one heading closes the other, and
          you can close the last one too.
        </p>
      </CardDocsSection>

      <CardDocsSection title="Multiple">
        <CardDocsPreview>
          <MultipleExample />
        </CardDocsPreview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">type=&quot;multiple&quot;</code> keeps every opened panel open
          until its own trigger is clicked again.
        </p>
      </CardDocsSection>

      <CardDocsSection title="Custom trigger">
        <CardDocsPreview>
          <CustomTriggerExample />
        </CardDocsPreview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">asChild</code> on{' '}
          <code className="font-mono">DimeAccordionTrigger</code> so a{' '}
          <code className="font-mono">DimeButton</code> is the trigger — email and overflow actions
          on the same row stay independent.
        </p>
      </CardDocsSection>
    </div>
  );
}
